import { Buffer } from 'buffer'
/**
 * Takes `search` from `location.search` and returns the value matching the
 * given param.
 *
 * @param  {string}      search   Search from the URI location
 * @param  {string}      param    Desired param to search
 *
 * @return {string|null}          The value from given param name
 */
export const getSearchParam = (search, param) => {
  const regex = new RegExp(`${param}=([^&]*)`);

  return search.match(regex) && search.match(regex)[1];
};

export const getIn = (obj = {}, path) =>
  path.reduce((xs, x) =>
    (xs && xs[x]) ? xs[x] : null, obj);

export const curriedGetIn = path => obj =>
  path.reduce((xs, x) =>
    (xs && xs[x]) ? xs[x] : null, obj);

export const mapFilter = (obj, fn) =>
  Object
    .keys(obj)
    .reduce((acc, key) =>
      (fn(obj[key]) ? Object.assign({}, acc, { [key]: obj[key] }) : acc),
      {});

export const mapIsEmpty = (obj) => !obj || !Object.keys(obj).length;

export const toArray = (obj) => Object.keys(obj).map((key) => obj[key]);

export const sortByKey = (obj) =>
  Object
    .keys(obj)
    .sort()
    .reduce((acc, key) =>
      Object.assign({}, acc, { [key]: obj[key] }),
      {});

export const isString = s => typeof s === 'string';

export const isNumber = n => typeof n === 'number';

export const isObject = o => o !== null && typeof o === 'object';

export const isBoolean = b => typeof b === 'boolean';

export const isFile = f => f !== null && f instanceof File;

export const toString = (s = "") => s !== null && s.toString && s.toString();

export const toNumber = (n = 0) => n !== null && parseInt(n, 10);

export const toLowerCase = (s = "") =>
  s !== null && s.toLowerCase && s.toLowerCase();

export const toRgba =
  (hex = "#FFFFFF", alpha = .88) =>
    `rgba(
      ${parseInt(hex.substring(1,3),16)},
      ${parseInt(hex.substring(3,5),16)},
      ${parseInt(hex.substring(5,7),16)},
      ${alpha}
    )`;

export const compare = (objA, objB, path, isDesc) => {
  const bandA = objA[path] ? objA[path].toUpperCase() : "";
  const bandB = objB[path] ? objB[path].toUpperCase() : "";

  let comparison = 0;
  if (bandA > bandB) {
    comparison = 1;
  } else if (bandA < bandB) {
    comparison = -1;
  }
  return (isDesc ? -1 : 1)*comparison ;
}

/**
 * Returns true only for absolute http(s) URLs. Used as the scheme allowlist for
 * any user/owner-supplied URL that ends up in an `href`. Rejects dangerous
 * schemes such as `javascript:`, `data:`, `vbscript:`, and `file:` that would
 * otherwise execute when a gallery visitor clicks the link (stored DOM-XSS).
 *
 * Parsed WITHOUT a base URL so that protocol-relative (`//evil.com`) and
 * relative inputs throw and are rejected — a CTA link must be a full http(s)
 * URL, never something resolved against the current origin.
 *
 * @param  {string}  urlString  The candidate URL.
 * @return {boolean}            Whether the URL is an absolute http(s) URL.
 */
export const isSafeHttpUrl = (urlString) => {
  if (typeof urlString !== "string") return false;
  const trimmed = urlString.trim();
  if (!trimmed) return false;
  try {
    const protocol = new URL(trimmed).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch (err) {
    return false;
  }
};

/**
 * Render-side guard for owner-supplied link URLs. Returns the URL only when it
 * is a safe http(s) URL, otherwise `undefined` so the consuming `href` is
 * dropped entirely rather than rendering an executable `javascript:` payload.
 *
 * @param  {string}            urlString  The candidate URL.
 * @return {string|undefined}             The URL if safe, else `undefined`.
 */
export const getSafeHref = (urlString) =>
  isSafeHttpUrl(urlString) ? urlString : undefined;

export const getValidUrl = (urlString) => {
  // Scheme allowlist first — the regex below matches scheme-less, domain-shaped
  // strings but never anchored the protocol, so an absolute `javascript:`/
  // `data:` URL with a domain-like tail could slip through. Reject any absolute
  // URL whose scheme is not http(s) outright before the lenient pattern runs.
  if (typeof urlString === "string" && /^[a-z][a-z\d+.-]*:/i.test(urlString)) {
    return isSafeHttpUrl(urlString) ? urlString : "";
  }

  var urlPattern = new RegExp(
    "^(\\/\\/)?" + // validate protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // validate domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // validate OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // validate port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // validate query string
      "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // validate fragment locator
  if (!!urlPattern.test(urlString)) {
    return urlString;
  }

  return "";
};

export const getFetchParams = (params) => {
  return params
    ? `?${Object.keys(params)
        .map((k) =>
          k === "page"
            ? `${k}=${params[k]}`
            : params[k]
            ? `${k}=${params[k]}`
            : null
        )
        .filter((x) => x)
        .join("&")}`
    : "";
};

/**
 * `#rgb`, `rgb`, `#rrggbb` or `rrggbb` → `#rrggbb`. CSS accepts the three-digit
 * short form, and a channel's saved color can be stored that way; without the
 * expansion the contrast math below reads an empty channel and returns NaN.
 */
function toLongHex(hexColor) {
  const hex = hexColor.replace(/^#/, "");
  return hex.length === 3
    ? `#${hex
        .split("")
        .map((c) => c + c)
        .join("")}`
    : `#${hex}`;
}

export function getContrastColor(hexColor) {
  hexColor = toLongHex(hexColor);

  const r = parseInt(hexColor.substr(1, 2), 16);
  const g = parseInt(hexColor.substr(3, 2), 16);
  const b = parseInt(hexColor.substr(5, 2), 16);

  // ITU-R BT.601 / W3C AERT luma (0.299R+0.587G+0.114B); 128 is the 8-bit
  // midpoint used to pick black vs white text on the private-gallery gate.
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 128 ? "black" : "white";
}

/** WCAG 2.1 relative luminance of a `#rrggbb`/`#rgb` (with or without `#`) color. */
function relativeLuminance(hexColor) {
  const hex = toLongHex(hexColor).slice(1);
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(hex.substr(i, 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Black or white, whichever has the higher WCAG contrast ratio against
 * `hexColor`. Unlike getContrastColor's luma midpoint, the better of the two
 * always clears 4.5:1 (the worst case, a mid gray, is about 4.6:1), so small
 * text set in it meets WCAG 2.1 AA on any background.
 */
export function getAccessibleTextColor(hexColor) {
  const l = relativeLuminance(hexColor);
  const onWhite = 1.05 / (l + 0.05);
  const onBlack = (l + 0.05) / 0.05;
  return onBlack >= onWhite ? "black" : "white";
}
